import React from "react";
import {
  Typography,
  ThemeProvider,
  Theme,
  StyledEngineProvider,
  Grid,
  GridProps,
} from "@mui/material";
import PFATheme from "../../PFATheme";
import { MediaCard } from "../MediaCard";
import { AppUrl } from "../../config";
import { Redirect, Link } from "react-router-dom";
import Breadcrumbs from "../../containers/Breadcrumbs";
import { appTitles } from "../../common/services/Utilities";
import NameOrLocationImg from "../../common/assets/images/BankFind_NameOrLocation.svg";
import ApiImg from "../../common/assets/images/BankFind_APIForDataMiners.svg";
import EventsAndChangesImg from "../../common/assets/images/BankFind_EventsAndChanges.svg";
import HistoricalImg from "../../common/assets/images/BankFind_HistoricalBankData.svg";
import FailuresImg from "../../common/assets/images/BankFind_BankFailuresAndAssistanceData.svg";
import HelpImage from "../../common/assets/images/HelpLib.svg";
import SODImage from "../../common/assets/images/BFS_SOD_Main.svg";
import FinancialsImg from "../../common/assets/images/BankFind_Financials.svg";
import PeerGroupImg from "../../common/assets/images/SDI_BFS_LP_Icon.svg";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const theme = PFATheme;

const LandingPageIntro = (
  <>
    <Typography variant="h1">BankFind Suite</Typography>
    <Typography variant="body2">
      BankFind Suite is a way for users to search the FDIC’s extensive data
      records. BankFind Suite allows you to locate current and former
      FDIC-insured banking institutions by name, FDIC certificate number,
      website and/or by location. The Suite also allows a user to follow the
      history and financial trends of an individual institution, group of
      institutions, or the industry as a whole. The FDIC is currently
      modernizing our public-facing offerings to provide an improved
      functionality and a more intuitive user experience through an iterative
      process. Updates with improvements to functionality and data access will
      continually be released as development progresses. Release notes are
      available{" "}
      <Link id="release-notes-link" to={"/help?helpTopic=release-notes"}>
        here
      </Link>
      .
    </Typography>
  </>
);

const gridItem: GridProps = {
  item: true,
  md: 4,
  xs: 12,
  sm: 12,
};

export default function BankFindSuite() {
  useInitialPageLoad("homePage", true);
  const urlParams = new URLSearchParams(window.location.search);
  const stateParam = urlParams.get("state") ?? "";
  const codeParam = urlParams.get("code") ?? "";
  const [redirect, setRedirect] = React.useState("/");

  React.useState(() => {
    if (stateParam) {
      const codePrefix = stateParam.includes("?") ? "&" : "?";
      const code = codeParam ? `${codePrefix}code=${codeParam}` : "";
      setRedirect(`${stateParam.split("/bankfind-suite")[1]}${code}`);
    }
  });

  return (
    <>
      <Redirect to={redirect} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Breadcrumbs currentPageBreadCrumbTitle={"BankFind Suite"} />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          {LandingPageIntro}

          <Grid container spacing={3} style={{ paddingBottom: 24 }}>
            <Grid {...gridItem}>
              <MediaCard
                img={HelpImage}
                title={"Help Library"}
                href={AppUrl("Help")}
                imageHeight={175}
                description={
                  "Your BankFind Suite information source for everything from variables and definitions, to helpful tutorials, to information on the historic data, and more."
                }
                id={"Help"}
              />
            </Grid>
            <Grid {...gridItem}>
              <MediaCard
                img={NameOrLocationImg}
                title={appTitles("bankFind")}
                href={AppUrl("BankFind")}
                description={
                  "Search for banks past and present by name, URL and FDIC Cert or find banks located within ZIPs, Cities, or States."
                }
                id={"bankFind"}
              />
            </Grid>
            <Grid {...gridItem}>
              <MediaCard
                img={FinancialsImg}
                title={"Run Financial Reports by Institution"}
                href={AppUrl("FinancialReporting")}
                imageHeight={125}
                description={
                  "Use institution-reported data to create financial reports that reveal performance trends over time."
                }
                id={"financials"}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3} style={{ paddingBottom: 12 }}>
            <Grid {...gridItem}>
              <MediaCard
                img={PeerGroupImg}
                title={"Peer Group Comparisons"}
                href={AppUrl("FinancialReporting")}
                imageHeight={175}
                description={
                  "Deepen your understanding of past and current financial trends by comparing institutions and peer groupings."
                }
                id={"peerGroup"}
              />
            </Grid>
            <Grid {...gridItem}>
              <MediaCard
                img={SODImage}
                title={"Summary of Deposits"}
                href={AppUrl("SOD")}
                imageHeight={175}
                description={
                  "The annual survey of branch office deposits (as of June 30) for all FDIC-insured institutions, including insured U.S. branches of foreign banks."
                }
                id={"SOD"}
              />
            </Grid>
            <Grid {...gridItem}>
              <MediaCard
                img={ApiImg}
                title={appTitles("bulkData")}
                href={AppUrl("BulkData")}
                description={
                  "Robust resources for acquiring the data you need to build apps and perform analysis on your own."
                }
                id={"API"}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid {...gridItem}>
              <MediaCard
                img={EventsAndChangesImg}
                title={appTitles("oscr")}
                href={AppUrl("OSCR")}
                description={
                  "Openings? Closings? Mergers? See all of the structural changes that have happened by institution and industry-wide."
                }
                id={"oscr"}
              />
            </Grid>
            <Grid {...gridItem}>
              <MediaCard
                img={HistoricalImg}
                title={appTitles("historical")}
                href={AppUrl("HSOB")}
                imageHeight={104}
                description={
                  "Annual and summary of financial and structural data for all FDIC-insured institutions since 1934."
                }
                id={"history"}
              />
            </Grid>
            <Grid {...gridItem}>
              <MediaCard
                img={FailuresImg}
                title={appTitles("failures")}
                href={AppUrl("Failures")}
                imageHeight={136}
                description={
                  "Get a complete look at bank failures and assistance transactions of FDIC-insured institutions from 1934 to the present."
                }
                id={"failure"}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </StyledEngineProvider>
    </>
  );
}
